import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Typography, Box, Button, Paper, Divider, Alert, AlertTitle, Grid } from '@mui/material';
import { Error, ArrowBack, Refresh, ShoppingCart } from '@mui/icons-material';
import { db } from '../../firebase/config';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

const PaymentFailure = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [errorDetails, setErrorDetails] = useState(null);
  const [orderReference, setOrderReference] = useState(null);
  
  // Get error details and order reference from URL query params and Firestore
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const reference = queryParams.get('reference') || queryParams.get('ref');
    const errorMessage = queryParams.get('error') || 'Payment processing failed';
    const errorCode = queryParams.get('error_code') || 'unknown';
    
    setOrderReference(reference);
    setErrorDetails({
      message: errorMessage,
      code: errorCode,
      timestamp: new Date().toISOString()
    });
    
    // Check if we have a payment status in sessionStorage first
    const checkSessionStorage = () => {
      if (!reference) return null;
      
      try {
        const storedPaymentStatus = sessionStorage.getItem(`payment_status_${reference}`);
        if (storedPaymentStatus) {
          const statusData = JSON.parse(storedPaymentStatus);
          console.log('Found payment status in sessionStorage:', statusData);
          
          if (statusData.status === 'failed' && (statusData.error || statusData.errorCode)) {
            return {
              message: statusData.error || errorMessage,
              code: statusData.errorCode || errorCode,
              timestamp: statusData.timestamp || new Date().toISOString(),
              firestoreId: statusData.firestoreId || null,
              apiResponse: statusData.apiResponse || null
            };
          }
        }
      } catch (storageErr) {
        console.warn('Error reading payment status from sessionStorage:', storageErr);
      }
      
      return null;
    };
    
    // Try to fetch additional error details from Firestore
    const fetchFailedPaymentDetails = async () => {
      if (!reference) return;
      
      // First check sessionStorage
      const sessionData = checkSessionStorage();
      if (sessionData) {
        setErrorDetails(sessionData);
        
        // If we have a Firestore ID from session data, try to fetch the order directly
        if (sessionData.firestoreId) {
          try {
            const docRef = doc(db, 'orders', sessionData.firestoreId);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              const orderData = docSnap.data();
              console.log('Found order in Firestore using firestoreId from session:', sessionData.firestoreId);
              
              // If we have more detailed error info in Firestore, use it
              if (orderData.paymentDetails && orderData.paymentDetails.error) {
                setErrorDetails(prev => ({
                  ...prev,
                  message: orderData.paymentDetails.error.message || prev.message,
                  code: orderData.paymentDetails.error.code || prev.code,
                  details: orderData.paymentDetails.error.details || null,
                  orderData: orderData
                }));
              }
              return; // We found what we needed
            }
          } catch (e) {
            console.warn('Error fetching order by firestoreId:', e);
          }
        }
      }
      
      // If we didn't find anything in sessionStorage or couldn't fetch by firestoreId,
      // continue with the query approach
      try {
        // First try to find by direct Firestore ID
        try {
          const docRef = doc(db, 'orders', reference);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const orderData = docSnap.data();
            console.log('Found order using reference as Firestore ID');
            
            // If we have more detailed error info, use it
            if (orderData.paymentDetails && orderData.paymentDetails.error) {
              setErrorDetails(prev => ({
                ...prev,
                message: orderData.paymentDetails.error.message || prev.message,
                code: orderData.paymentDetails.error.code || prev.code,
                details: orderData.paymentDetails.error.details || null,
                orderData: orderData
              }));
            }
            return; // We found what we needed
          }
        } catch (e) {
          console.log('Not a valid Firestore ID, searching by reference...');
        }
        
        // If not found directly, try queries
        const ordersRef = collection(db, 'orders');
        
        // Try to find by localOrderId in orders collection
        let paymentQuery = query(
          ordersRef,
          where('localOrderId', '==', reference)
        );
        
        let querySnapshot = await getDocs(paymentQuery);
        
        if (!querySnapshot.empty) {
          const paymentData = querySnapshot.docs[0].data();
          console.log('Found order using localOrderId query');
          
          // If we have more detailed error info, use it
          if (paymentData.paymentDetails && paymentData.paymentDetails.error) {
            setErrorDetails(prev => ({
              ...prev,
              message: paymentData.paymentDetails.error.message || prev.message,
              code: paymentData.paymentDetails.error.code || prev.code,
              details: paymentData.paymentDetails.error.details || null,
              orderData: paymentData
            }));
          }
        }
      } catch (err) {
        console.error('Error fetching payment failure details:', err);
      }
    };
    
    fetchFailedPaymentDetails();
  }, [location.search]);
  
  const handleTryAgain = () => {
    // Navigate back to checkout with the cart preserved
    navigate('/marketplace/checkout');
  };
  
  const handleReturnToCart = () => {
    navigate('/marketplace/cart');
  };
  
  const handleContinueShopping = () => {
    navigate('/marketplace');
  };
  
  // Common error messages and suggested actions
  const getErrorHelp = (errorCode) => {
    switch(errorCode) {
      case 'insufficient_funds':
        return 'Your mobile money account does not have enough funds. Please add money to your account and try again.';
      case 'invalid_number':
        return 'The mobile number provided is invalid. Please check your number and try again.';
      case 'transaction_rejected':
        return 'The transaction was rejected by the mobile money provider. This could be due to account restrictions or daily limits.';
      case 'timeout':
        return 'The payment request timed out. Please check your network connection and try again.';
      default:
        return 'There was an issue processing your payment. Please try again or contact customer support for assistance.';
    }
  };
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Error color="error" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Payment Failed
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            We couldn't process your payment
          </Typography>
        </Box>
        
        <Alert severity="error" sx={{ mb: 4 }}>
          <AlertTitle>Error Details</AlertTitle>
          {errorDetails?.message || 'Payment processing failed'}
          {errorDetails?.code !== 'unknown' && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Error code: {errorDetails?.code}
            </Typography>
          )}
        </Alert>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            What happened?
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body1" paragraph>
            {getErrorHelp(errorDetails?.code)}
          </Typography>
          
          <Typography variant="body1" paragraph>
            Your order has been saved, and no payment has been processed. You can try again with a different payment method or contact our support team for assistance.
          </Typography>
          
          {orderReference && (
            <Typography variant="body2" color="text.secondary">
              Reference: {orderReference}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            What can you do now?
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Try Again
                </Typography>
                <Typography variant="body2" paragraph>
                  Attempt the payment again, perhaps with a different mobile money account or after ensuring you have sufficient funds.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<Refresh />}
                  onClick={handleTryAgain}
                  fullWidth
                >
                  Try Again
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Review Your Cart
                </Typography>
                <Typography variant="body2" paragraph>
                  Return to your cart to review your items or choose a different payment method.
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  startIcon={<ShoppingCart />}
                  onClick={handleReturnToCart}
                  fullWidth
                >
                  Return to Cart
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Continue Shopping
                </Typography>
                <Typography variant="body2" paragraph>
                  Browse more products in our marketplace. Your cart will be saved for later.
                </Typography>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  startIcon={<ArrowBack />}
                  onClick={handleContinueShopping}
                  fullWidth
                >
                  Continue Shopping
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            If you continue to experience issues, please contact our support team at{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              wilbert@kihembo.com
            </Box>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentFailure;
